// --- React & Redux
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import Input from '@dtci/spellbook/Input';
// --- Hooks & Store
import { useTemplatesActions } from 'datastage/store/templates/useTemplates';
// --- Styles
import './styles.scss';


// Initialize checked state from field groups
// Returns an object mapping each groupId to its checked state and a nested object of fieldIds to their checked states.
const getCheckedStateFromFieldGroups = (groups) =>
    groups.reduce((acc, group) => {
        acc[group.groupId] = {
            checked: group.hasOwnProperty('checked') ? group.checked : true,
            fields: group.fields.reduce((fAcc, field) => {
                fAcc[field.fieldId] = field.hasOwnProperty('checked') ? field.checked : true;
                return fAcc;
            }, {}),
        };
        return acc;
    }, {});

// Extract selected field keys from checked state
const getSelectedFieldKeys = (checkedState) =>
    Object.entries(checkedState).flatMap(([_, group]) =>
        Object.entries(group.fields)
            .filter(([_, checked]) => checked)
            .map(([fieldId]) => fieldId)
    );

// Highlight matching text in search results
const highlightMatch = (text, search) => {
    if (!search) return text;
    const regex = new RegExp(`(${search})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
        regex.test(part) ? (
            <span key={i} className="custom-field-highlight">
                {part}
            </span>
        ) : (
            part
        ),
    );
};


/**
 * Custom Field Tree Component
 *
 * Features:
 * - Renders a tree structure of custom fields with search functionality
 * - Allows selection/deselection of fields and field groups
 * - Supports saving, updating, and deleting custom field lists
 *
 * @returns {Element}
 * @constructor
 */
export const CustomFieldTree = React.forwardRef((props, ref) => {
    const { fieldGroups, selectedCustomList, checkedState: reduxCheckedState, isDrawerOpen } = useSelector(
        (state) => state.templates,
    );
    const [searchTerm, setSearchTerm] = useState('');
    // Local checked state - changes here don't affect Redux until Apply
    const [localCheckedState, setLocalCheckedState] = useState({});

    // Reset local state when drawer opens - copy from Redux
    useEffect(() => {
        if (isDrawerOpen) {
            setSearchTerm('');
            // Initialize local state from Redux when drawer opens
            if (Object.keys(reduxCheckedState).length) {
                setLocalCheckedState(JSON.parse(JSON.stringify(reduxCheckedState)));
            }
        }
    }, [isDrawerOpen]);

    // Track expanded/collapsed state for each group (all expanded by default)
    const [expandedGroups, setExpandedGroups] = useState(() =>
        fieldGroups?.reduce((acc, group) => {
            acc[group.groupId] = true;
            return acc;
        }, {}) || {}
    );
    // Ref for the select all checkbox to control indeterminate state
    const selectAllRef = useRef(null);
    const { updateTemplates } = useTemplatesActions();

    // Use local state for checkbox changes
    const checkedState = localCheckedState;
    const setCheckedState = setLocalCheckedState;

    // Expose functions via ref so parent can call them
    React.useImperativeHandle(ref, () => ({
        applyChanges: () => {
            updateTemplates({ checkedState: localCheckedState });
            // Update customFields as well
            if (fieldGroups?.length && Object.keys(localCheckedState).length) {
                const { updatedCustomFields } = getUpdatedFields(localCheckedState);
                updateTemplates({ customFields: updatedCustomFields });
            }
        },
        getSelectedFieldKeys: () => getSelectedFieldKeys(localCheckedState)
    }));

    // Initialize local checked state when fieldGroups or selectedCustomList changes
    useEffect(() => {
        if (!fieldGroups?.length) return;
        
        if (
            selectedCustomList &&
            Array.isArray(selectedCustomList.fieldKeys) &&
            selectedCustomList.fieldKeys.length > 0
        ) {
            const newCheckedState = fieldGroups.reduce((acc, group) => {
                const hasField = group.fields.some((f) =>
                    selectedCustomList.fieldKeys.includes(f.fieldId),
                );
                acc[group.groupId] = {
                    checked: hasField,
                    fields: group.fields.reduce((fAcc, field) => {
                        fAcc[field.fieldId] = selectedCustomList.fieldKeys.includes(field.fieldId);
                        return fAcc;
                    }, {}),
                };
                return acc;
            }, {});
            setLocalCheckedState(newCheckedState);
            updateTemplates({ checkedState: newCheckedState });
        } else if (!Object.keys(reduxCheckedState).length) {
            const initialState = getCheckedStateFromFieldGroups(fieldGroups);
            setLocalCheckedState(initialState);
            updateTemplates({ checkedState: initialState });
        }
    }, [selectedCustomList, fieldGroups]);

    // Sync expanded state when field groups change (ensure new groups are expanded)
    useEffect(() => {
        if (fieldGroups?.length > 0) {
            setExpandedGroups((prev) => {
                const newExpanded = { ...prev };
                fieldGroups.forEach((group) => {
                    if (!(group.groupId in newExpanded)) {
                        newExpanded[group.groupId] = true;
                    }
                });
                return newExpanded;
            });
        }
    }, [fieldGroups]);


    // Get updated field groups and custom fields based on checked state
    const getUpdatedFields = (checkedState) => {
        const updatedFieldGroups = fieldGroups.map((group) => ({
            ...group,
            checked: checkedState[group.groupId]?.checked ?? false,
            fields: group.fields.map((field) => ({
                ...field,
                checked: checkedState[group.groupId]?.fields?.[field.fieldId] ?? false,
            })),
        }));

        const updatedCustomFields = updatedFieldGroups.flatMap((group) =>
            group.fields.filter((f) => f.checked).map((f) => f.fieldId),
        );

        return { updatedFieldGroups, updatedCustomFields };
    };

    // Handle parent group checkbox change
    // Toggle all child fields accordingly
    const handleParentChange = (groupId) => {
        setCheckedState((prev) => {
            const group = prev[groupId];
            const newChecked = !group.checked;
            const groupFields = fieldGroups.find((g) => g.groupId === groupId).fields;
            const newFields = Object.fromEntries(
                groupFields.map((field) => [field.fieldId, field.required ? true : newChecked]),
            );
            return {
                ...prev,
                [groupId]: { checked: newChecked, fields: newFields },
            };
        });
    };

    // Handle child field checkbox change
    // Update parent group checked state accordingly
    const handleChildChange = (groupId, fieldId) => {
        setCheckedState((prev) => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                fields: {
                    ...prev[groupId].fields,
                    [fieldId]: !prev[groupId].fields[fieldId],
                },
            },
        }));
    };

    // Filter field groups and fields based on search term (memoized)
    const filteredGroups = useMemo(() => {
        if (!searchTerm) return fieldGroups;
        return fieldGroups
            .map((group) => {
                const groupMatches =
                    group.groupName &&
                    group.groupName.toLowerCase().includes(searchTerm.toLowerCase());
                const filteredFields = group.fields.filter(
                    (field) =>
                        field.fieldName &&
                        field.fieldName.toLowerCase().includes(searchTerm.toLowerCase()),
                );
                if (groupMatches) {
                    return { ...group, fields: filteredFields };
                }
                if (filteredFields.length > 0) {
                    return { ...group, fields: filteredFields };
                }
                return null;
            })
            .filter(Boolean);
    }, [searchTerm, fieldGroups]);

    // Toggle expand/collapse state for a group
    const toggleGroupExpanded = (groupId) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };

    // Handle select/deselect all checkbox change
    const handleSelectAllChange = (e) => {
        const checked = e.target.checked;
        const newState = Object.fromEntries(
            fieldGroups.map((group) => [
                group.groupId,
                {
                    checked,
                    fields: Object.fromEntries(
                        group.fields.map((field) => [
                            field.fieldId,
                            field.required ? true : checked,
                        ]),
                    ),
                },
            ]),
        );
        setCheckedState(newState);
    };

    // Calculate select all checkbox state and counts
    const { selectAllState, totalFields, selectedCount } = useMemo(() => {
        if (!fieldGroups?.length || !Object.keys(checkedState).length) {
            return { 
                selectAllState: { allChecked: false, someChecked: false },
                totalFields: 0,
                selectedCount: 0
            };
        }
        
        let total = 0;
        let checked = 0;
        
        fieldGroups.forEach((group) => {
            group.fields.forEach((field) => {
                total++;
                if (checkedState[group.groupId]?.fields?.[field.fieldId]) {
                    checked++;
                }
            });
        });
        
        const allChecked = checked === total;
        const someChecked = checked > 0 && checked < total;
        
        return { 
            selectAllState: { allChecked, someChecked },
            totalFields: total,
            selectedCount: checked
        };
    }, [checkedState, fieldGroups]);

    // Set indeterminate state on the select all checkbox
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = selectAllState.someChecked;
        }
    }, [selectAllState]);

    // Handle empty state
    if (!fieldGroups || fieldGroups.length === 0) {
        return <div>No custom fields available.</div>;
    }

    return (
        <div className="custom-field-layout">
            {/* Sticky Row: Select All + Search */}
            <div className="custom-field-controls-sticky">
                <label className="custom-field-select-all-label">
                    <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={selectAllState.allChecked}
                        onChange={handleSelectAllChange}
                    />
                    <span className="custom-field-select-all-text">Select/Deselect All</span>
                </label>
                <Input
                    value={searchTerm || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    width="medium"
                    id="searchTerm"
                    name="searchTerm"
                    placeholder="Search fields..."
                />
            </div>

            {/* Scrollable Tree Container */}
            <div className="custom-field-tree-container">
                {filteredGroups.length === 0 ? (
                    <div className="custom-field-empty">No matching fields found.</div>
                ) : (
                    filteredGroups.map((group) => (
                        <div key={group.groupId} className="custom-field-group">
                            <div className="custom-field-group-row">
                                <i
                                    className={`fa-solid ${expandedGroups[group.groupId] ? 'fa-chevron-down' : 'fa-chevron-right'} custom-field-chevron`}
                                    onClick={() => toggleGroupExpanded(group.groupId)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === 'Enter' && toggleGroupExpanded(group.groupId)}
                                    aria-label={expandedGroups[group.groupId] ? 'Collapse group' : 'Expand group'}
                                />
                                <label className="custom-field-group-label">
                                    <input
                                        type="checkbox"
                                        checked={checkedState[group.groupId]?.checked}
                                        onChange={() => handleParentChange(group.groupId)}
                                    />
                                    <span className="custom-field-group-name">
                                        {highlightMatch(group.groupName, searchTerm)}
                                    </span>
                                    <span className="custom-field-group-count">
                                        ({Object.values(checkedState[group.groupId]?.fields || {}).filter(Boolean).length}/{group.fields.length})
                                    </span>
                                </label>
                            </div>
                            {expandedGroups[group.groupId] && (
                                <div className="custom-field-list">
                                    {group.fields.map((field) => (
                                        <div key={field.fieldId} className="custom-field-item-row">
                                            <label className="custom-field-item-label">
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        checkedState[group.groupId]?.fields?.[field.fieldId]
                                                    }
                                                    disabled={field.required}
                                                    onChange={() =>
                                                        handleChildChange(group.groupId, field.fieldId)
                                                    }
                                                />
                                                <span className="custom-field-item-name">
                                                    {highlightMatch(field.fieldName, searchTerm)}
                                                </span>
                                                {field.required && (
                                                    <span className="required-asterisk">*</span>
                                                )}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
});
